<template>
  <article id="custom-text-model-editor" class="section__content">
    <router-link to="/custom-text-model" class="btn-back">
      <SvgIconDecorative icon="arrowLeft" />
      <VisibleText> Back <span class="sr-only"> to custom text model groups </span> </VisibleText>
    </router-link>
    <div>
      <div class="custom-text-model-editor__header">
        <InlineEditTextInput
          label="topic model group name"
          input-id="topic-model-group-name-input"
          input-name="topic-model-group-name"
          :input-value="topicModelGroup.name"
          @save="saveName"
          v-if="profile && topicModelGroup.owner == profile.django_ref_id"
        >
          <h2>
            {{ topicModelGroup.name }}
          </h2>
        </InlineEditTextInput>
        <h1 v-if="profile && topicModelGroup.owner != profile.django_ref_id">
          {{ topicModelGroup.name }}
        </h1>
      </div>
      <div style="margin-top: -1rem; margin-bottom: 1rem">
        <div v-if="ownerEmail" style="display: flex; flex-direction: row">
          <VisibleText>Created by</VisibleText>:
          <span style="margin-left: 1rem">{{ ownerEmail }}</span>
        </div>
      </div>
      <div
        v-if="
          profile &&
          topicModelGroup.owner !== profile.django_ref_id &&
          hasAccess &&
          topicModelGroup.access_type == 'public__team'
        "
      >
        <h3>
          <VisibleText> Viewing as a team member </VisibleText>
        </h3>
      </div>
      <div v-if="!hasAccess">
        <h3>
          <VisibleText> You do not have permission to access this group. </VisibleText>
        </h3>
      </div>
      <div>
        <VisibleText> This custom text model is set to the following access level: </VisibleText>
        <select
          aria-label="access level"
          class="select"
          id="select-access-level"
          @change="changeAccessLevel"
          v-model="topicModelGroup.access_type"
          style="width: 50%; margin-top: 1rem"
          :disabled="profile && topicModelGroup.owner !== profile.django_ref_id"
        >
          <option
            v-for="level in Object.keys(ACCESS_TYPE_TO_DESC)"
            :key="level"
            :value="level"
            :data-parent="$options.name"
          >
            {{ translate(ACCESS_TYPE_TO_DESC[level].name, $options.name) }} -
            {{ translate(ACCESS_TYPE_TO_DESC[level].desc, $options.name) }}
          </option>
        </select>
        <div style="margin-top: 1rem" v-if="topicModelGroup.access_type == 'public__team'">
          <VisibleText> For this team: </VisibleText>
          <select
            aria-label="access level"
            class="select"
            id="select-access-level-team"
            style="width: 50%; margin-top: 1rem"
            v-model="selectedTeam"
            @change="changeTeamAccessLevel"
            :disabled="profile && topicModelGroup.owner !== profile.django_ref_id"
          >
            <option
              v-for="team in teams"
              :key="team.team_name"
              :value="team"
              :data-parent="$options.name"
            >
              {{ team.team_name }}
            </option>
          </select>
        </div>
      </div>

      <!--------------------------------------------
      |
      | TOPIC GROUP DETAILS
      |
      --------------------------------------------->
      <CustomTopicModelEditorDetails
        :topic-model-group="topicModelGroup"
        :isPublicReadOnly="!isOwner || !topicModelGroup.access_type == 'public__team'"
      />
    </div>

    <!--------------------------------------------
    |
    | TOPIC MODEL OPTIONS
    |
    --------------------------------------------->
    <CustomTopicModelEditorOptions
      :selected-topic-models="selectedTopicModels"
      :bulk_upload_received="bulk_upload_status.received"
      @add-theme="isAddThemeVisible = true"
      @deleted-themes="deletedThemes"
      @deselect="selectedTopicModels = []"
      @refresh-topic-models="refreshTopicModels"
      @select="selectAll"
      @toggle-editing="readOnlyTable = !readOnlyTable"
      @toggle-uploading="toggleUploadingNewThemes"
      @toggle-downloading="toggleDownloadThemes"
      v-if="isOwner || topicModelGroup.access_type == 'public__team'"
    />

    <!--------------------------------------------
    |
    | Uploading new themes
    |
    --------------------------------------------->

    <div v-if="uploadingNewThemes" class="uploading-themes-container">
      <div v-if="!bulk_upload_status.received">
        <div class="upload-mode-toggle-wrapper">
          <div class="upload-mode-toggle">
            <label :class="{ active: uploadMode === 'spreadsheet' }">
              <input type="radio" value="spreadsheet" v-model="uploadMode" />
              Spreadsheet mode
            </label>

            <label :class="{ active: uploadMode === 'delimited' }">
              <input type="radio" value="delimited" v-model="uploadMode" />
              Delimited mode
            </label>
          </div>
        </div>

        <div v-if="uploadMode == 'spreadsheet'">
          <div class="upload-guide">
            <div class="upload-content">
              <div class="upload-text">
                <h2 class="upload-title">How to bulk upload keywords from a file</h2>

                <p class="upload-instructions">
                  Upload a spreadsheet where the
                  <strong>first column contains theme names</strong> and all
                  <strong>other columns contain keywords</strong>. Each row represents one theme.
                  Leave cells blank if you don’t need them. Duplicate keywords are automatically
                  ignored.
                </p>
                <p class="upload-instructions">
                  This feature only accepts the first sheet in your file.
                </p>
              </div>

              <div class="example-table-wrapper">
                <strong>Example spreadsheet:</strong>
                <table class="example-table">
                  <thead>
                    <tr>
                      <th>Theme</th>
                      <th>Keyword 1</th>
                      <th>Keyword 2</th>
                      <th>Keyword 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Satisfaction</td>
                      <td>friendly staff</td>
                      <td>helpful</td>
                      <td>polite</td>
                    </tr>
                    <tr>
                      <td>Wait Times</td>
                      <td>long wait</td>
                      <td>slow service</td>
                      <td>queue</td>
                    </tr>
                    <tr>
                      <td>Staff Attitude</td>
                      <td>rude</td>
                      <td>unprofessional</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="upload-guide">
            <div class="upload-content">
              <div class="upload-text">
                <h2 class="upload-title">
                  How to bulk upload keywords from a file using delimiters (semi-colons)
                </h2>

                <p class="upload-instructions">
                  Upload a spreadsheet where the first column contains theme names and the second
                  column contains all keywords separated by semicolons (<strong>;</strong>). Each
                  row represents one theme. Duplicates will be removed.
                </p>
                <p class="upload-instructions">
                  This feature only accepts the first sheet in your file.
                </p>
              </div>

              <div class="example-table-wrapper">
                <strong>Example spreadsheet:</strong>
                <table class="example-table">
                  <thead>
                    <tr>
                      <th>Theme</th>
                      <th>Keywords (semicolon-separated)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Satisfaction</td>
                      <td>friendly staff; helpful; polite</td>
                    </tr>
                    <tr>
                      <td>Wait Times</td>
                      <td>long wait; slow service; queue</td>
                    </tr>
                    <tr>
                      <td>Staff Attitude</td>
                      <td>rude; unprofessional</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="dataset-file-upload-container">
          <label
            :class="['center-align-parent', { 'upload-error': Boolean(errMsg.file) }]"
            id="dataset-file-upload-label"
            for="dataset-file-upload"
            ref="file-label"
          >
            <span class="center-align-child" ref="file-upload-label">
              <UploadCloudIcon />
              <span id="dataset-file-upload-label-formats">
                <VisibleText> Supported formats: .xls, .xlsx, .ods</VisibleText>
              </span>
            </span>
          </label>
          <input
            aria-describedby="file-err"
            type="file"
            id="dataset-file-upload"
            ref="file"
            accept=".sav, .xls, .xlsx, .csv, .ods"
            :aria-invalid="Boolean(errMsg.file)"
            :disabled="readOnly"
            @change="onFileChange"
          />
        </div>
      </div>
      <div v-if="bulk_upload_status.received">
        <h2 class="upload-title">Upload complete</h2>
        <p><strong>New themes: </strong> {{ bulk_upload_status.new_themes }}</p>
        <p><strong>New keywords: </strong> {{ bulk_upload_status.new_keywords }}</p>
        <p><strong>Duplicates ignored: </strong> {{ bulk_upload_status.duplicate_keywords }}</p>
      </div>
    </div>

    <!--------------------------------------------
    |
    | NEW TOPIC MODEL
    |
    --------------------------------------------->
    <CustomTopicModelEditorNewTheme
      :topic-model-group="topicModelGroup"
      v-if="isAddThemeVisible && hasAccess"
      @close="isAddThemeVisible = false"
      @new-theme-created="newThemeCreated"
    />

    <!--------------------------------------------
    |
    | THEMES FILTER
    |
    --------------------------------------------->
    <div class="themes-toolbar">
      <input
        v-model="searchQuery"
        type="text"
        class="themes-search"
        placeholder="Search themes..."
      />

      <div class="sort-controls">
        <label>Sort by:</label>

        <select v-model="sortKey">
          <option value="name">Theme Name</option>
          <option value="keywords"># Keywords</option>
          <option value="example_sentences"># example sentences</option>
          <option value="snapshots"># Snapshots</option>
          <option value="has_model">Has Model</option>
        </select>

        <div
          style="
            width: 1.5rem;
            height: 1.5rem;
            border: 1px solid var(--outline-colour);
            background: var(--secondary-ui-colour);
            border-radius: 4px;
          "
        >
          <button
            class="tooltip__hoverable icon-only-btn center-align-parent"
            style="margin: 0"
            :aria-labelledby="`ctm-sort-label`"
            :type="'button'"
            @click="changeSortDirection()"
          >
            <SvgIcon
              class="center-align-child"
              :aria-hidden="true"
              :focusable="false"
              :icon="sortDirection === 'asc' ? 'ascending' : 'descending'"
              style="margin-left: -0.25rem; margin-top: -0.25rem"
            />
          </button>
        </div>
      </div>
    </div>

    <!--------------------------------------------
    |
    | THEMES GRID
    |
    --------------------------------------------->
    <ListGrid
      :custom-grid-style="{
        gridTemplateColumns: '4rem repeat(4, minmax(auto, 1fr))',
      }"
      :headers="headers"
      :list="filteredAndSortedTopicModels"
      :key="themesGridKey"
      :isLoadingStatus="fetchingComplete ? 'not-loading' : 'loading'"
    >
      <template #default="{ item, index }">
        <ListGridItem col="0" :headers="headers" :row="index">
          <span class="form-checkbox-label">
            <input
              type="checkbox"
              tabindex="-1"
              :aria-label="item.name"
              :value="item._id.$oid"
              v-model="selectedTopicModels"
            />
          </span>
        </ListGridItem>
        <ListGridItem col="1" :headers="headers" :row="index">
          <ThemeName :oid="item._id.$oid" :read-only="readOnlyTable" :theme-name="item.name" />
        </ListGridItem>
        <ListGridItem col="2" :headers="headers" :row="index">
          <Keywords
            :oid="item._id.$oid"
            :keywords="
              item.keyword_objects ? item.keyword_objects.map((keyword) => keyword.keyword) : []
            "
            :read-only="readOnlyTable"
            :isPublicReadOnly="isPublicReadOnly"
          />
        </ListGridItem>
        <ListGridItem col="3" :headers="headers" :row="index">
          <!-- No example sentences -->
          <div v-if="!item.example_sentences || item.example_sentences.length === 0">
            <em class="example-empty">
              Link notes from a project's text analysis to this model to generate example sentences.
            </em>
          </div>
          <!-- Example sentences exist -->
          <div v-else>
            <div class="example-header">From previously‑tagged projects</div>
            <ExampleQuestions
              :oid="item._id.$oid"
              :example-questions="item.example_sentences"
              :read-only="readOnlyTable"
              :isPublicReadOnly="isPublicReadOnly"
            />
          </div>
        </ListGridItem>
        <ListGridItem col="4" :headers="headers" :row="index">
          <div class="theme-learning-summary">
            <div class="snapshot-card">
              <h4>Snapshots</h4>

              <div v-if="item.learning?.has_snapshot">
                <div class="ml-status">
                  <span :class="mlStatusClass(item.learning)">
                    {{ mlStatusText(item.learning) }}
                  </span>
                  <span class="ml-score">
                    ({{ Math.round(item.learning.stats.ml_readiness_score * 100) }}%)
                  </span>
                </div>

                <div class="mini-stats">
                  <div>Positives: {{ item.learning.stats.positives }}</div>
                  <div>Negatives: {{ item.learning.stats.negatives }}</div>
                  <div>Snapshots: {{ item.learning.total_snapshots }}</div>
                </div>
                <button class="table__toolbar-btn approve" @click="showMachineLearnings(item)">
                  <VisibleText>Show snapshots</VisibleText>
                </button>
              </div>

              <div v-else>No snapshots yet</div>
            </div>

            <div class="model-card">
              <h4>Trained Model</h4>

              <div v-if="item.training?.has_model">
                <div class="model-status good">Has trained model</div>
                <div class="mini-stats">
                  <div>Last trained: {{ formatDateTime(item.training.model) }}</div>
                  <div>Total training artefacts: {{ item.training.model.n_total }}</div>
                </div>
              </div>
              <div v-else>No trained model yet</div>
              <button
                v-if="item.learning?.has_snapshot || item.training?.has_model"
                class="table__toolbar-btn approve"
                @click="showThisTrainingModal(item)"
              >
                Show / train model
              </button>
            </div>
          </div>
        </ListGridItem>
      </template>
    </ListGrid>
    <AllSnapshots
      :hasMetadata="
        topicModelGroup.include_ml_metadata ? topicModelGroup.include_ml_metadata : false
      "
      :filteredSnapshots="allSnapshots"
      :show="showSnapshotModal"
      @close-modal="closeSnapshotsModal()"
      @dropSnapshot="dropSnapshot($event)"
      :teamMembers="teamMembers"
      :profile="profile"
      :model_theme_id="model_theme_id"
    />
    <TrainModel
      ref="CustomTopicTrainedModel"
      :show="showTrainingModal"
      @close-modal="closeTrainingModal()"
      @deleteMachineLearningModel="deleteMachineLearningModel($event)"
      :themeForTraining="selectedTheme"
      :trainedModel="selectedTraining"
      @model-trained="refreshTrainedModelForTheme($event)"
    />
  </article>
</template>

<script>
import moment from 'moment';

// Components
import CustomTopicModelEditorDetails from './CustomTopicModelEditorDetails.vue';
import CustomTopicModelEditorNewTheme from './CustomTopicModelEditorNewTheme.vue';
import CustomTopicModelEditorOptions from './CustomTopicModelEditorOptions.vue';
import InlineEditTextInput from '@/components/UI/InlineEditTextInput.vue';
import ListGrid from '@/components/UI/ListGrid.vue';
import ListGridItem from '@/components/UI/ListGridItem.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';
import SvgIcon from '@/components/UI/Svg/SvgIcon.vue';

import ThemeName from './Components/ThemeName.vue';
import Keywords from './Components/Keywords.vue';
import ExampleQuestions from './Components/ExampleQuestions.vue';
import AllSnapshots from './Components/AllSnapshots.vue';
import TrainModel from './Components/TrainModel.vue';

import UploadCloudIcon from '@/assets/icons/Datasets/upload-cloud.svg';

// Mixins
import ConfirmMixin from '@/utils/mixins/confirmMixin.js';
import UserMixin from '@/utils/mixins/userMixin';

// Services
import { fetchGroup, fetchTopicModels } from '../Utils/fetch.js';
import { ACCESS_TYPE_TO_DESC } from '../Utils/consts.js';
import customTopicModelService from '@/services/customTopicModelService.js';
import TeamsService from '@/services/teamsService.js';

export default {
  name: 'CustomTopicModelEditor',
  mixins: [ConfirmMixin, UserMixin],
  components: {
    CustomTopicModelEditorDetails,
    CustomTopicModelEditorNewTheme,
    CustomTopicModelEditorOptions,
    InlineEditTextInput,
    ListGrid,
    ListGridItem,
    SvgIconDecorative,
    SvgIcon,
    ThemeName,
    Keywords,
    ExampleQuestions,
    AllSnapshots,
    UploadCloudIcon,
    TrainModel,
  },
  data() {
    return {
      fetchingComplete: false,
      headers: ['', 'Theme Name', 'Keywords', 'Example Sentences', 'Machine Learning'],
      isAddThemeVisible: false,
      readOnlyTable: true,
      // bulk upload
      uploadingNewThemes: false,
      isLoading: false,
      errMsg: {
        file: null,
      },
      file: null,
      uploadMode: 'spreadsheet',
      bulk_upload_status: {
        received: false,
        new_themes: 0,
        new_keywords: 0,
        duplicate_keywords: 0,
      },
      selectedTopicModels: [],
      topicModels: [], // These are the keywords
      topicModelGroup: {}, // These are the groups of keywords under one banner
      selectedTeam: {},
      themesGridKey: this.$pigeonline.createUUID(),
      ACCESS_TYPE_TO_DESC: ACCESS_TYPE_TO_DESC,
      CUSTOM_TOPIC_MODEL_SERVICE: new customTopicModelService(this.$pigeonline),
      TEAMS_SERVICE: new TeamsService(this.$pigeonline),
      ownerEmail: '',
      hasAccess: false,
      teamMembers: [],
      showSnapshotModal: false,
      allSnapshots: [],
      showTrainingModal: false,
      selectedTheme: {},
      selectedTraining: {},
      searchQuery: '',
      sortKey: 'name',
      sortDirection: 'asc',
      model_theme_id: '',
    };
  },

  async mounted() {
    this.topicModels = [];
    this.topicModelGroup = {};
    const modelId = this.$route.params.id;
    await this.checkForGroup(modelId);
    // await this.checkGroupsForMachineLearning();
    // Get all team members (one way to get owner email)
    this.findTeamMembers();
  },
  async created() {
    // await this.refreshTopicModels()
  },
  computed: {
    matchingTeam() {
      if (
        this.topicModelGroup &&
        this.topicModelGroup.created_by &&
        this.topicModelGroup.created_by.organization &&
        this.topicModelGroup.access_type == 'public__team'
      ) {
        if (this.teams && this.teams.length) {
          let filteredTheme = this.teams.filter(
            (team) => team._id.$oid == this.topicModelGroup.created_by.organization,
          );
          if (filteredTheme.length) {
            return filteredTheme[0];
          }
        }
      }
      return false;
    },
    isOwner() {
      if (this.profile && this.profile.django_ref_id) {
        if (this.profile.django_ref_id == this.topicModelGroup.owner) {
          return true;
        }
      }
      return false;
    },
    isPublicReadOnly() {
      let status = false;
      if (
        this.topicModelGroup &&
        this.topicModelGroup.access_type == 'public__all' &&
        this.isOwner == false
      ) {
        status = true;
      }
      return status;
    },
    filteredAndSortedTopicModels() {
      let list = [...this.topicModels];

      // --- SEARCH ---
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();

        list = list.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            (t.keyword_objects || []).some((k) => k.keyword.toLowerCase().includes(q)) ||
            (t.example_questions || []).some((eq) => eq.toLowerCase().includes(q)),
        );
      }

      // --- SORT ---
      list.sort((a, b) => {
        let A, B;

        switch (this.sortKey) {
          // case "last_modified_on":
          //   A = new Date(a.last_modified_on);
          //   B = new Date(b.last_modified_on);
          //   break;

          default: // name
            A = a.name.toLowerCase();
            B = b.name.toLowerCase();

          case 'name':
            A = a.name.toLowerCase();
            B = b.name.toLowerCase();
            break;

          case 'keywords':
            A = a.keyword_objects?.length || 0;
            B = b.keyword_objects?.length || 0;
            break;

          case 'example_sentences':
            A = a.example_sentences.length;
            B = b.example_sentences.length;
            break;

          case 'snapshots':
            A = a.learning?.total_snapshots || 0;
            B = b.learning?.total_snapshots || 0;
            break;

          case 'has_model':
            A = a.training?.has_model ? 1 : 0;
            B = b.training?.has_model ? 1 : 0;
            break;
        }

        if (A < B) return this.sortDirection === 'asc' ? -1 : 1;
        if (A > B) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return list;
    },
  },
  methods: {
    onError() {
      this.setConfirmText({
        btn: 'okay',
        title: 'Error Occured',
        message: 'Sorry, there was a problem loading the page. Please try again.',
      });
      this.setConfirmSourceComponent('topicModelEditor');
      this.setConfirmType('error');
      this.setConfirmIsVisible(true);
    },
    async checkForGroup(modelId) {
      let fetchedModelGroup = await fetchGroup(modelId);

      if (
        fetchedModelGroup['project_type'] &&
        fetchedModelGroup['project_type'] == 'custom_topic_model_group'
      ) {
        this.topicModelGroup = fetchedModelGroup;
        this.hasAccess = true;
      }
    },
    // async checkIsOwner() {
    //   const userId = await this.$store.getters["user/getProfile"].django_ref_id
    //   if (userId == this.topicModelGroup.owner) {
    //     this.isOwner = true
    //   }
    // },
    async findTeamMembers() {
      this.teamMembers = [];
      this.teamMembers = await this.TEAMS_SERVICE.getAllTeamMembers({
        include_details: true,
      });
    },
    changeAccessLevel() {
      if (this.isOwner == true) {
        if (
          this.topicModelGroup.access_type == 'public__all' ||
          this.topicModelGroup.access_type == 'private'
        ) {
          let full_name = '';
          if (this.profile) {
            if (this.profile.fullname) {
              full_name = this.profile.fullname;
            }
          }
          if (full_name) {
            this.topicModelGroup['created_by'] = {
              name: full_name,
              organization: '',
            };
            this.$pigeonline.projects.update(this.topicModelGroup);
          }
        }
      }
    },
    changeTeamAccessLevel() {
      if (this.isOwner == true) {
        // if teams, change created_by to include org name
        if (this.topicModelGroup.access_type == 'public__team') {
          let team_id = '';
          if (this.selectedTeam && this.selectedTeam['_id']) {
            team_id = this.selectedTeam._id.$oid;
          }
          let team_name = '';
          if (this.selectedTeam && this.selectedTeam['team_name']) {
            team_name = this.selectedTeam.team_name;
          }
          if (team_id && team_name) {
            this.topicModelGroup['created_by'] = {
              organization: team_id,
              name: team_name,
            };
          }
          this.$pigeonline.projects.update(this.topicModelGroup);
        }
      }
    },
    async getOwnerEmail() {
      let email = await this.$pigeonline.getUserEmail();
      this.ownerEmail = email;
    },
    newThemeCreated() {
      this.refreshTopicModels();
      this.themesGridKey = this.$pigeonline.createUUID();
    },
    saveName(name) {
      this.topicModelGroup.name = name;
      this.$pigeonline.projects.update(this.topicModelGroup);
    },
    selectAll() {
      this.selectedTopicModels = this.topicModels.map((topic) => topic._id.$oid);
    },
    async deletedThemes() {
      this.selectedTopicModels = [];
      this.refreshTopicModels();
      this.themesGridKey = this.$pigeonline.createUUID();
    },
    async refreshTopicModels() {
      if (!this.hasAccess) return;

      this.fetchingComplete = false;
      this.topicModels = [];

      const models = await fetchTopicModels(this.$route.params.id);

      // Build unified objects
      const unified = [];

      for (const model of models) {
        let theme_id = model._id?.$oid || model._id;

        const payload = { model_theme_id: theme_id };

        const learning = await this.CUSTOM_TOPIC_MODEL_SERVICE.learningStatus(payload);
        const training = await this.CUSTOM_TOPIC_MODEL_SERVICE.getTrainedModel(payload);

        unified.push({
          ...structuredClone(model),
          learning: structuredClone(learning || null),
          training: structuredClone(training || null),
          example_sentences: structuredClone(learning?.example_sentences || []),
        });
      }

      this.topicModels = unified;
      this.topicModelGroup.themes = unified.map((m) => m._id.$oid);
      this.fetchingComplete = true;
    },

    async refreshTrainedModelForTheme(theme_id) {
      const payload = { model_theme_id: theme_id };

      // Find the index of this theme in unified topicModels
      const idx = this.topicModels.findIndex((t) => {
        const id = t._id?.$oid || t._id;
        return id === theme_id;
      });

      if (idx === -1) return;

      // Fetch updated trained model
      const trained = await this.CUSTOM_TOPIC_MODEL_SERVICE.getTrainedModel(payload);

      if (trained) {
        // Update only the training field inside the unified object
        this.topicModels[idx].training = structuredClone(trained);

        // Update selectedTraining if this theme is currently selected
        if (
          this.selectedTheme &&
          (this.selectedTheme._id?.$oid || this.selectedTheme._id) === theme_id
        ) {
          this.selectedTraining = this.topicModels[idx].training;
        }

        // Refresh modal if open
        if (this.$refs.CustomTopicTrainedModel) {
          this.$refs.CustomTopicTrainedModel.refreshModel();
        }
      }
    },

    changeSortDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      this.selectedTopicModels = [];
      this.refreshTopicModels();
      this.themesGridKey = this.$pigeonline.createUUID();
    },

    toggleUploadingNewThemes() {
      if (this.uploadingNewThemes) {
        this.bulk_upload_status.received = false;
        this.bulk_upload_status['new_themes'] = 0;
        this.bulk_upload_status['new_keywords'] = 0;
        this.bulk_upload_status['duplicate_keywords'] = 0;
      }
      this.uploadingNewThemes = !this.uploadingNewThemes;
    },
    async toggleDownloadThemes() {
      const group_id = this.topicModelGroup.id;
      this.CUSTOM_TOPIC_MODEL_SERVICE.download_excel(group_id);
    },
    async onFileChange() {
      this.isLoading = true;
      try {
        const input = this.$refs.file;
        const file = input.files[0]; // <-- the real file

        const formData = new FormData();
        formData.append('file', file);
        const group_id = this.topicModelGroup.id;

        let delimiter = null;
        if (this.uploadMode == 'delimited') {
          delimiter = ';';
        }

        const response = await this.CUSTOM_TOPIC_MODEL_SERVICE.bulk_update(
          formData,
          group_id,
          delimiter,
        );
        if (response) {
          this.bulk_upload_status['received'] = true;
          if (response['data'] && response['data']['themes_added']) {
            this.bulk_upload_status['new_themes'] = response['data']['themes_added'];
          }
          if (response['data'] && response['data']['keywords_added']) {
            this.bulk_upload_status['keywords_added'] = response['data']['keywords_added'];
          }
          if (response['data'] && response['data']['duplicate_themes_ignored']) {
            this.bulk_upload_status['duplicate_keywords'] =
              response['data']['duplicate_themes_ignored'];
          }
          this.topicModels = [];
          this.topicModelGroup = {};
          const modelId = this.$route.params.id;
          await this.checkForGroup(modelId);
          await this.refreshTopicModels();
        }
      } catch (e) {
        alert('There was an error uploading the file. Please try again');
        throw new Error('CustomTopicModelEditor:upload ' + e.message);
      }
      this.isLoading = false;
    },

    async showMachineLearnings(item) {
      const theme_id = item._id.$oid || item._id;

      this.model_theme_id = theme_id;

      const payload = { model_theme_id: theme_id };
      const result = await this.CUSTOM_TOPIC_MODEL_SERVICE.getAllSnapshots(payload);

      if (result?.snapshots) {
        this.allSnapshots = result.snapshots;
        this.showSnapshotModal = true;
      }
    },

    async showThisTrainingModal(item) {
      this.showTrainingModal = true;
      this.selectedTheme = item;
      this.selectedTraining = item.training;
    },

    mlStatusClass(learning) {
      if (!learning?.has_snapshot) return 'ml-none';

      if (learning.stats.ml_ready) return 'ml-ready';
      if (learning.stats.ml_readiness_score > 0.3) return 'ml-progress';
      return 'ml-not-ready';
    },

    mlStatusText(learning) {
      if (!learning?.has_snapshot) return 'No ML data';

      if (learning.stats.ml_ready) return 'Ready for ML training';
      if (learning.stats.ml_readiness_score > 0.3) return 'Needs more data';
      return 'Not enough data';
    },
    closeSnapshotsModal() {
      this.allSnapshots = [];
      this.showSnapshotModal = false;
    },
    closeTrainingModal() {
      this.showTrainingModal = false;
    },
    async dropSnapshot(snapshot) {
      const snapshotId = snapshot._id.$oid || snapshot._id;

      const payload = { snapshot_id: snapshotId };

      const result = await this.CUSTOM_TOPIC_MODEL_SERVICE.dropSnapshot(payload);

      if (result.success) {
        this.allSnapshots = this.allSnapshots.filter((s) => s._id !== snapshotId);
        this.refreshTopicModels();
      }
    },

    async deleteMachineLearningModel(model_id) {
      if (!model_id) return;

      const payload = { model_id: model_id };

      const result = await this.CUSTOM_TOPIC_MODEL_SERVICE.deleteTrainedModel(payload);

      if (result.deleted) {
        this.selectedTraining.has_model = false;
        this.selectedTraining.model = {};
        if (this.$refs.CustomTopicTrainedModel) {
          this.$refs.CustomTopicTrainedModel.refreshModel();
        }
      }
    },

    formatDateTime: (value, language) => {
      let currentLang = '';
      if (language) {
        currentLang = language;
      }
      if (value) {
        if (currentLang === 'fr') {
          return moment(value).locale('fr').format('MMMM D, YYYY hh:mm A').toString();
        } else {
          return moment(value).format('MMMM D, YYYY hh:mm A').toString();
        }
      }
    },
  },
  watch: {
    confirmIsVisible: function (val) {
      // user clicked "leave page"
      if (
        !val &&
        !this.confirmClickedClose &&
        !this.confirmStatus &&
        this.confirmSourceComponent === 'topicModelEditor' &&
        this.confirmType === 'warning'
      ) {
        this.$router.push({ name: 'projects' });
      }
    },

    hasAccess: function (val) {
      if (val) {
        this.refreshTopicModels();
        this.changeAccessLevel();
      }
    },
    isOwner: function (val) {
      if (val) {
        this.getOwnerEmail();
      }
    },
    matchingTeam: function (val) {
      if (val) {
        this.selectedTeam = val;
      }
    },
    teamMembers: function (val) {
      if (val) {
        let filtered_team_members = val.filter((member) => member.id == this.topicModelGroup.owner);
        if (filtered_team_members.length) {
          this.ownerEmail = filtered_team_members[0].email;
        }
      }
    },
    topicModelGroup: {
      deep: true,
      handler: function (value) {
        if (value && Object.keys(value).length > 0) this.$pigeonline.projects.update(value);
      },
    },
    sortKey: function (val) {
      if (val) {
        this.refreshTopicModels();
      }
    },
  },
};
</script>
<style>
.uploading-themes-container {
  margin-bottom: 1rem;
}

.upload-guide {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.upload-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2rem;
  max-width: 1000px;
}

.upload-text {
  max-width: 500px;
}

.upload-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.4rem;
  color: #222;
}

.upload-instructions {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.45;
}

.example-table-wrapper {
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 1rem;
  background: #fafafa;
  min-width: 350px;
}

.example-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.example-table th {
  background: #f0f0f0;
  font-weight: 600;
  padding: 8px;
  border-bottom: 1px solid #dcdcdc;
  text-align: left;
}

.example-table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.example-table tr:last-child td {
  border-bottom: none;
}

.example-table td:first-child {
  font-weight: 600;
  color: #444;
}

.upload-mode-toggle-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 auto 2rem auto;
}

.upload-mode-toggle {
  display: inline-flex;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  overflow: hidden;
}

.upload-mode-toggle label {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  background: #f6f8fa;
  color: #444;
  border-right: 1px solid #d0d7de;
  user-select: none;
  transition:
    background 0.15s,
    color 0.15s;
}

.upload-mode-toggle label:last-child {
  border-right: none;
}

.upload-mode-toggle input {
  display: none;
}

.upload-mode-toggle label.active {
  background: var(--primary);
  color: white;
}

.ml-ready {
  color: #2ecc71;
  font-weight: bold;
}
.ml-progress {
  color: #f1c40f;
}
.ml-not-ready {
  color: #e67e22;
}
.ml-none {
  color: #bdc3c7;
}

@media (max-width: 750px) {
  .upload-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .upload-text {
    max-width: 500px;
  }
}

.theme-learning-summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.snapshot-card,
.model-card {
  background: #f8f9ff;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e0e3ff;
}

.snapshot-card h4,
.model-card h4 {
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
}

.model-status.good {
  color: #2e7d32;
  font-weight: 600;
}

.model-status.none {
  color: #9e9e9e;
}

.mini-stats {
  margin: 0.5rem 0;
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.themes-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
}

.themes-search {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline-colour);
  border-radius: 6px;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-controls select {
  padding: 0.4rem;
  border-radius: 6px;
  border: 1px solid var(--outline-colour);
}

.example-empty {
  color: var(--base-colour);
  opacity: 0.7;
  font-style: italic;
  display: block;
  padding: 0.25rem 0;
}

.example-header {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--base-colour);
}
</style>
